import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Megaphone, LockKey, Warning, Trash, Archive, Plus, ChartBar, Paperclip, FilmSlate } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatWorkDate } from "@/lib/time";
import { AnnouncementMediaPicker } from "@/components/AnnouncementMediaPicker";
import { AnnouncementTrackingDialog } from "@/components/AnnouncementTrackingDialog";

const VIEWS = [["active", "Active"], ["scheduled", "Scheduled"], ["expired", "Expired"], ["archived", "Archived"], ["all", "All"]];
const PRIORITY_META = {
    normal: { label: "Normal", badge: "bg-slate-100 text-slate-700 border-slate-200" },
    important: { label: "Important", badge: "bg-amber-100 text-amber-800 border-amber-200" },
    urgent: { label: "Urgent", badge: "bg-red-100 text-[#E6002C] border-red-200" },
};
const STATE_BADGE = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
    scheduled: "bg-blue-100 text-[#0066D9] border-blue-200",
    expired: "bg-slate-100 text-slate-600 border-slate-200",
    archived: "bg-slate-100 text-slate-500 border-slate-200",
};
const EXPIRY_PRESETS = [["1", "1 day"], ["7", "7 days"], ["30", "30 days"], ["90", "3 months"]];
const BLANK = {
    title: "", message: "", priority: "normal", scope: "all", ids: [],
    preset: "30", custom_expires: "", schedule: false, publish_at: "",
    requires_pin: false, requires_acknowledgment: false,
};

const isoIn = (days, from) => {
    const d = from ? new Date(from) : new Date();
    d.setDate(d.getDate() + Number(days));
    return d.toISOString();
};

export default function AdminAnnouncements() {
    const [view, setView] = useState("active");
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [perms, setPerms] = useState({});
    const [recipients, setRecipients] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(BLANK);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [tracking, setTracking] = useState(null);
    const [media, setMedia] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tick, setTick] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams({ view });
            if (search.trim()) p.set("search", search.trim());
            const { data } = await api.get(`/announcements?${p.toString()}`);
            setRows(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [view, search]);

    useEffect(() => { load(); }, [load, tick]);
    useEffect(() => {
        api.get("/announcements/permissions").then(({ data }) => setPerms(data)).catch(() => setPerms({}));
        api.post("/announcements/recipients/preview", { recipient_scope: "all" })
            .then(({ data }) => setRecipients(data.recipients || [])).catch(() => setRecipients([]));
    }, []);

    const selected = useMemo(
        () => (form.scope === "all" ? recipients : recipients.filter((r) => form.ids.includes(r.id))),
        [form.scope, form.ids, recipients],
    );
    const missingPin = selected.filter((r) => !r.has_pin);
    const expiresAt = form.preset === "custom"
        ? (form.custom_expires ? new Date(form.custom_expires).toISOString() : "")
        : isoIn(form.preset, form.schedule ? form.publish_at : null);

    const publish = async () => {
        if (!form.title.trim()) return toast.error("A title is required.");
        if (!form.message.trim() && !media) {
            return toast.error("Add a message, or attach a photo or video, so recipients have something to read or see.");
        }
        if (!selected.length) return toast.error("Select at least one recipient.");
        if (!expiresAt) return toast.error("Choose when this announcement expires.");
        setSaving(true);
        try {
            await api.post("/announcements", {
                title: form.title, message: form.message, priority: form.priority,
                recipient_scope: form.scope, technician_ids: form.scope === "all" ? [] : form.ids,
                publish_at: form.schedule && form.publish_at ? new Date(form.publish_at).toISOString() : null,
                expires_at: expiresAt,
                requires_pin: form.requires_pin,
                requires_acknowledgment: form.requires_acknowledgment,
                media_id: media?.media_id || null,
            });
            toast.success(`Announcement published to ${selected.length} technician${selected.length === 1 ? "" : "s"}.`);
            setOpen(false); setForm(BLANK); setMedia(null); setTick((t) => t + 1);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not publish");
        } finally {
            setSaving(false);
        }
    };

    const archive = async (row) => {
        try {
            await api.post(`/announcements/${row.id}/archive?archived=${row.state !== "archived"}`);
            toast.success(row.state === "archived" ? "Restored." : "Archived.");
            setTick((t) => t + 1);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    };

    const remove = async () => {
        try {
            await api.delete(`/announcements/${confirmDelete.id}`);
            toast.success("Announcement deleted.");
            setConfirmDelete(null); setTick((t) => t + 1);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    };

    const openTracking = async (row) => {
        try {
            const { data } = await api.get(`/announcements/${row.id}/tracking`);
            setTracking(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">Communications</div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Announcements
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm">
                        Send notices to one, several or all active technicians. Every recipient is tracked separately.
                    </p>
                </div>
                {perms.can_publish_announcements && (
                    <Button onClick={() => setOpen(true)} className="bg-[#003366] hover:bg-[#0066D9] h-11"
                        data-testid="new-announcement-button">
                        <Plus size={16} className="mr-2" /> New announcement
                    </Button>
                )}
            </div>

            <Card className="border-[#E2E8F0]">
                <CardContent className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="flex flex-wrap gap-2">
                        {VIEWS.map(([key, label]) => (
                            <Button key={key} size="sm" variant={view === key ? "default" : "outline"}
                                className={view === key ? "bg-[#003366]" : "border-[#E2E8F0] text-slate-600"}
                                onClick={() => setView(key)} data-testid={`announcements-view-${key}`}>
                                {label}
                            </Button>
                        ))}
                    </div>
                    <Input placeholder="Search title or message…" value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-10 sm:max-w-xs sm:ml-auto border-[#E2E8F0]"
                        data-testid="announcements-search-input" />
                </CardContent>
            </Card>

            {loading ? (
                <Skeleton className="h-56 rounded-md" />
            ) : rows.length === 0 ? (
                <Card className="border-[#E2E8F0]">
                    <CardContent className="p-10 text-center text-sm text-slate-500" data-testid="announcements-empty">
                        No announcements in this view.
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3" data-testid="announcements-list">
                    {rows.map((r) => (
                        <Card key={r.id} className="border-[#E2E8F0]" data-testid={`announcement-row-${r.id}`}>
                            <CardContent className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Megaphone size={16} weight="duotone" className="text-[#0066D9]" />
                                        <span className="font-semibold text-[#1F2A44] truncate">{r.title}</span>
                                        <Badge className={`${PRIORITY_META[r.priority].badge} border text-[11px]`}>
                                            {PRIORITY_META[r.priority].label}
                                        </Badge>
                                        <Badge className={`${STATE_BADGE[r.state]} border text-[11px] capitalize`}>{r.state}</Badge>
                                        {r.requires_pin && (
                                            <Badge className="bg-slate-900 text-white border-slate-900 text-[11px]">
                                                <LockKey size={11} className="mr-1" /> Private
                                            </Badge>
                                        )}
                                        {r.requires_acknowledgment && (
                                            <Badge className="bg-blue-50 text-[#0066D9] border border-blue-200 text-[11px]">
                                                Acknowledgment
                                            </Badge>
                                        )}
                                        {r.media_type && (
                                            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[11px]"
                                                data-testid={`media-chip-${r.id}`}>
                                                {r.media_type === "video"
                                                    ? <><FilmSlate size={11} className="mr-1" /> Video</>
                                                    : <><Paperclip size={11} className="mr-1" /> Photo</>}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1.5">
                                        {r.recipient_count} recipient{r.recipient_count === 1 ? "" : "s"} ·
                                        {" "}published {formatWorkDate(r.publish_at.slice(0, 10))} ·
                                        {" "}expires {formatWorkDate(r.expires_at.slice(0, 10))} · by {r.created_by_name}
                                    </div>
                                    {r.progress && r.progress.recipients > 0 && (
                                        <div className="text-xs text-[#003366] mt-1 font-medium"
                                            data-testid={`progress-${r.id}`}>
                                            Seen {r.progress.presented}/{r.progress.recipients}
                                            {r.requires_pin ? ` · Unlocked ${r.progress.unlocked}/${r.progress.recipients}` : ""}
                                            {r.requires_acknowledgment
                                                ? ` · Acknowledged ${r.progress.acknowledged}/${r.progress.recipients}`
                                                : ""}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {perms.can_view_announcement_tracking && (
                                        <Button size="sm" variant="outline" className="border-[#E2E8F0]"
                                            onClick={() => openTracking(r)} data-testid={`tracking-${r.id}`}>
                                            <ChartBar size={14} className="mr-1.5" /> Tracking
                                        </Button>
                                    )}
                                    {perms.can_manage_announcements && (
                                        <Button size="sm" variant="outline" className="border-[#E2E8F0]"
                                            onClick={() => archive(r)} data-testid={`archive-${r.id}`}>
                                            <Archive size={14} className="mr-1.5" />
                                            {r.state === "archived" ? "Restore" : "Archive"}
                                        </Button>
                                    )}
                                    {perms.can_delete_announcements && (
                                        <Button size="sm" variant="ghost" className="text-[#E6002C]"
                                            onClick={() => setConfirmDelete(r)} data-testid={`delete-${r.id}`}>
                                            <Trash size={14} />
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Create */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="create-announcement-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>New announcement</DialogTitle>
                        <DialogDescription>Recipients are locked in when you publish.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Title</Label>
                            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                className="h-11 border-[#E2E8F0]" data-testid="announcement-title-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Message {media ? "(optional — the attachment can speak for itself)" : ""}
                            </Label>
                            <Textarea rows={5} value={form.message}
                                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                                className="border-[#E2E8F0]" data-testid="announcement-message-input" />
                            {!form.message.trim() && !media && (
                                <p className="text-[11px] text-slate-400" data-testid="announcement-content-hint">
                                    Add a message, or attach a photo or video — at least one is required.
                                </p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Priority</Label>                            <div className="flex gap-2">
                                {Object.entries(PRIORITY_META).map(([key, meta]) => (
                                    <Button key={key} type="button" size="sm"
                                        variant={form.priority === key ? "default" : "outline"}
                                        className={form.priority === key ? "bg-[#003366]" : "border-[#E2E8F0] text-slate-600"}
                                        onClick={() => setForm((f) => ({ ...f, priority: key }))}
                                        data-testid={`priority-${key}`}>{meta.label}</Button>
                                ))}
                            </div>
                        </div>

                        <AnnouncementMediaPicker media={media} onChange={setMedia} />

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Recipients</Label>
                                <span className="text-xs text-[#0066D9] font-medium" data-testid="recipient-count">
                                    {selected.length} selected
                                </span>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-[#1F2A44]">
                                <Checkbox checked={form.scope === "all"}
                                    onCheckedChange={(v) => setForm((f) => ({ ...f, scope: v ? "all" : "selected" }))}
                                    data-testid="select-all-technicians" />
                                Select all technicians ({recipients.length})
                            </label>
                            {form.scope !== "all" && (
                                <div className="max-h-44 overflow-y-auto border border-[#E2E8F0] rounded-md divide-y divide-slate-100">
                                    {recipients.map((r) => (
                                        <label key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                                            <Checkbox checked={form.ids.includes(r.id)}
                                                onCheckedChange={() => setForm((f) => ({
                                                    ...f,
                                                    ids: f.ids.includes(r.id) ? f.ids.filter((i) => i !== r.id) : [...f.ids, r.id],
                                                }))}
                                                data-testid={`recipient-${r.id}`} />
                                            <span className="flex-1 truncate">{r.full_name}</span>
                                            {!r.has_pin && <span className="text-[10px] uppercase text-amber-600">No PIN</span>}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Expires</Label>
                                <div className="flex flex-wrap gap-2">
                                    {[...EXPIRY_PRESETS, ["custom", "Custom"]].map(([key, label]) => (
                                        <Button key={key} type="button" size="sm"
                                            variant={form.preset === key ? "default" : "outline"}
                                            className={form.preset === key ? "bg-[#003366]" : "border-[#E2E8F0] text-slate-600"}
                                            onClick={() => setForm((f) => ({ ...f, preset: key }))}
                                            data-testid={`expiry-${key}`}>{label}</Button>
                                    ))}
                                </div>
                                {form.preset === "custom" && (
                                    <Input type="datetime-local" value={form.custom_expires}
                                        onChange={(e) => setForm((f) => ({ ...f, custom_expires: e.target.value }))}
                                        className="h-10 border-[#E2E8F0]" data-testid="custom-expiry-input" />
                                )}
                                <p className="text-[11px] text-slate-400">Maximum lifetime is 6 months.</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Publication</Label>
                                <label className="flex items-center gap-2 text-sm">
                                    <Switch checked={form.schedule}
                                        onCheckedChange={(v) => setForm((f) => ({ ...f, schedule: v }))}
                                        data-testid="schedule-switch" />
                                    {form.schedule ? "Schedule" : "Publish now"}
                                </label>
                                {form.schedule && (
                                    <Input type="datetime-local" value={form.publish_at}
                                        onChange={(e) => setForm((f) => ({ ...f, publish_at: e.target.value }))}
                                        className="h-10 border-[#E2E8F0]" data-testid="publish-at-input" />
                                )}
                            </div>
                        </div>

                        <div className="space-y-3 pt-3 border-t border-[#E2E8F0]">
                            <label className="flex items-start gap-3 text-sm">
                                <Switch checked={form.requires_pin}
                                    onCheckedChange={(v) => setForm((f) => ({ ...f, requires_pin: v }))}
                                    data-testid="requires-pin-switch" />
                                <span>
                                    Require PIN to view
                                    <span className="block text-xs text-slate-500">
                                        Title, message and media stay hidden until the recipient enters their own 4-digit PIN.
                                    </span>
                                </span>
                            </label>
                            <label className="flex items-start gap-3 text-sm">
                                <Switch checked={form.requires_acknowledgment}
                                    onCheckedChange={(v) => setForm((f) => ({ ...f, requires_acknowledgment: v }))}
                                    data-testid="requires-ack-switch" />
                                <span>
                                    Require acknowledgment before continuing
                                    <span className="block text-xs text-slate-500">
                                        The recipient must press Acknowledge &amp; Continue before using the app.
                                    </span>
                                </span>
                            </label>
                        </div>

                        {form.requires_pin && missingPin.length > 0 && (
                            <div className="flex gap-2.5 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900"
                                data-testid="missing-pin-warning">
                                <Warning size={18} className="shrink-0 mt-0.5" />
                                <div>
                                    <span className="font-semibold">{missingPin.length} recipient{missingPin.length === 1 ? " has" : "s have"} no PIN yet</span>
                                    {" "}and could never open this private announcement:{" "}
                                    {missingPin.slice(0, 6).map((r) => r.full_name).join(", ")}
                                    {missingPin.length > 6 ? ` +${missingPin.length - 6} more` : ""}.
                                </div>
                            </div>
                        )}

                        <div className="p-4 rounded-md bg-slate-50 border border-[#E2E8F0] text-sm space-y-1"
                            data-testid="publish-summary">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">
                                Before you publish
                            </div>
                            <div>Recipients: <strong>{selected.length} technician{selected.length === 1 ? "" : "s"}</strong></div>
                            <div>Priority: <strong>{PRIORITY_META[form.priority].label}</strong></div>
                            <div>PIN required: <strong>{form.requires_pin ? "Yes" : "No"}</strong></div>
                            <div>Acknowledgment required: <strong>{form.requires_acknowledgment ? "Yes" : "No"}</strong></div>
                            <div>Expires: <strong>{expiresAt ? new Date(expiresAt).toLocaleString() : "—"}</strong></div>
                            <div>Publication: <strong>{form.schedule && form.publish_at ? new Date(form.publish_at).toLocaleString() : "Immediately"}</strong></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E2E8F0]">Cancel</Button>
                        <Button onClick={publish}
                            disabled={saving || (form.requires_pin && missingPin.length > 0)
                                || (!form.message.trim() && !media)}
                            className="bg-[#003366] hover:bg-[#0066D9]" data-testid="publish-announcement-button">
                            {saving ? "Publishing…" : "Publish announcement"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Tracking */}
            <AnnouncementTrackingDialog tracking={tracking} onClose={() => setTracking(null)} />

            {/* Delete */}
            <Dialog open={Boolean(confirmDelete)} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
                <DialogContent className="sm:max-w-md" data-testid="delete-announcement-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#E6002C]">Delete announcement?</DialogTitle>
                        <DialogDescription>
                            This will permanently delete “{confirmDelete?.title}”, its recipient list, its tracking
                            records and any attached media. This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-[#E2E8F0]">Cancel</Button>
                        <Button onClick={remove} className="bg-[#E6002C] hover:bg-[#c50025]"
                            data-testid="confirm-delete-announcement">Delete permanently</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
